import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs));
};

export const isString = (value: unknown): value is string => typeof value === 'string';
export const isPlainObject = (value: unknown): value is object =>
  !!value && typeof value === 'object' && !Array.isArray(value);

export const getAllStringsInObject = (obj: object) => {
  const extractStrings = (value: unknown): string[] => {
    if (isString(value)) return [value];
    if (isPlainObject(value)) return getAllStringsInObject(value);
    return [];
  };
  return Object.values(obj).flatMap(extractStrings);
};

export const toStartCase = (name: string | undefined | null) => {
  if (!name) return '';
  return name
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

type TrySuccess<T> = readonly [null, T];
type TryFailure<E> = readonly [E, null];
export type TryResult<E, T> = TrySuccess<T> | TryFailure<E>;
export type TryResultAsync<E, T> = Promise<TryResult<E, T>>;

export const trySync = <E = Error, T = unknown>(operation: () => T): TryResult<E, T> => {
  try {
    const data = operation();
    return [null, data];
  } catch (error) {
    return [error as E, null];
  }
};

export const tryAsync = async <E = Error, T = unknown>(
  operation: Promise<T>,
): TryResultAsync<E, T> => {
  try {
    const value = await operation;
    return [null, value] as const;
  } catch (error) {
    return [error as E, null] as const;
  }
};

/** Mimics the result of Object.keys(...) */
export type keysOf<o> = o extends readonly unknown[]
  ? number extends o['length']
    ? `${number}`
    : keyof o & `${number}`
  : { [K in keyof o]: K extends string ? K : K extends number ? `${K}` : never }[keyof o];

export const keysOf = <o extends object>(o: o) => Object.keys(o) as keysOf<o>[];
